import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth, ROLE_LABELS } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Calculator, ClipboardList, KanbanSquare, Boxes,
  Users, Images, UserCog, Menu, X, LogOut, Scissors, Settings
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "kasir", "designer", "operator"], end: true },
  { to: "/kalkulator", label: "Kalkulator Harga", icon: Calculator, roles: ["admin", "kasir", "designer"] },
  { to: "/order", label: "Order & SPK", icon: ClipboardList, roles: ["admin", "kasir"] },
  { to: "/produksi", label: "Antrean Produksi", icon: KanbanSquare, roles: ["admin", "kasir", "designer", "operator"] },
  { to: "/inventory", label: "Stok Benang & Bahan", icon: Boxes, roles: ["admin", "designer", "operator"] },
  { to: "/pelanggan", label: "Pelanggan", icon: Users, roles: ["admin", "kasir"] },
  { to: "/desain", label: "Katalog Desain", icon: Images, roles: ["admin", "designer"] },
  { to: "/pengguna", label: "Manajemen Pengguna", icon: UserCog, roles: ["admin"] },
  { to: "/pengaturan", label: "Pengaturan Usaha", icon: Settings, roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const items = NAV.filter((n) => n.roles.includes(user?.role));

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-900/40">
          <Scissors className="h-5 w-5" />
        </div>
        <div className="leading-tight">
          <p className="font-heading text-sm font-extrabold text-white">Bordir<span className="text-blue-400">Kom</span></p>
          <p className="text-[11px] text-slate-400">Manajemen Usaha Bordir</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto no-scrollbar px-3 py-4 space-y-1">
        {items.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1)}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                isActive ? "bg-slate-800/80 text-white" : "text-slate-300 hover:bg-slate-800/50 hover:text-white"
              )
            }
          >
            <n.icon className="h-[18px] w-[18px]" />
            {n.label}
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-slate-800 p-4">
        <div className="flex items-center gap-3 rounded-lg bg-slate-800/50 p-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-sm font-bold text-white">
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
            <p className="truncate text-[11px] text-slate-400">{ROLE_LABELS[user?.role]}</p>
          </div>
          <button data-testid="btn-logout" onClick={handleLogout} className="text-slate-400 hover:text-rose-400 transition-colors">
            <LogOut className="h-[18px] w-[18px]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 bg-[#0F172A] fixed inset-y-0 left-0 z-30">
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-64 bg-[#0F172A]">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 lg:pl-64 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-md md:px-6 no-print">
          <button data-testid="btn-mobile-menu" className="lg:hidden text-slate-700" onClick={() => setOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1" />
          <StatusBadge value={user?.role} kind="role" className="capitalize" />
          <div className="hidden sm:flex items-center gap-2 pl-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <span className="text-sm font-semibold text-slate-700">{user?.name}</span>
          </div>
        </header>

        <main className="flex-1 p-4 md:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
